import filter from 'lodash/filter';
import find from 'lodash/find';
import map from 'lodash/map';
import Papa from 'papaparse';
import reduce from 'lodash/reduce';

import { getAllAgents } from './helpers';
import { Ancestry, getAncestralNames } from 'src/lib/hierarchy';
import { Role } from 'src/actions/roles/rolesResource';
import RoutingGroup from 'src/models/RoutingGroup/RoutingGroup';
import { SkillAncestry } from 'src/actions/skills/skillHierarchyResource';
import { Skill } from 'src/actions/skills/skillsResource';
import { skillHierarchyToAncestries } from 'src/actions/skills/skillsUtils';
import { Team } from 'src/actions/teams/teamsResource';
import { User } from 'src/models/Users/User';

interface ExportRow {
  name: string;
  email: string;
  roles?: string;
  inboxes?: string;
  teams?: string;
  deactivated: 'Yes' | 'No';
  stationid: string;
  skills?: string;
}

const agentUsersListToCsv = ({
  agentProfiles,
  agentSkills,
  routingGroups,
  skillHierarchy,
  skills,
  teams,
  users,
  allRoles,
}): string | never => {
  const userSkills = mergeSkillsWithHierarchy(skillHierarchy, skills);

  const userTeams = getAllAgents<Team>(teams);
  const userRoutingGroups = getAllAgents<RoutingGroup>(routingGroups);

  const usersRows: ExportRow[] = reduce(
    agentProfiles,
    (acc: ExportRow[], agent) => {
      const row = agentUserToExportRow(agent, users, agentSkills, userSkills, userTeams, userRoutingGroups, allRoles);
      if (row) {
        acc.push(row);
      }
      return acc;
    },
    []
  );

  return objToCsv(usersRows);
};

function getSkills(agent, agentSkills, skills) {
  const agentSkillsList = find(agentSkills, skill => skill.agentId === agent.id);
  if (!agentSkillsList) {
    return [];
  }
  return filter(skills, skill => agentSkillsList.skillIds?.includes(skill.id));
}

const agentUserToExportRow = (
  agent,
  users,
  agentSkills,
  skills,
  userTeams,
  userRoutingGroups,
  allRoles
): ExportRow | undefined => {
  const matchedUser: User | undefined = find(users, user => agent.id === user.id);
  if (!matchedUser) {
    return;
  }
  const agentUser = { ...agent, ...matchedUser };
  const userSkills = getSkills(agentUser, agentSkills, skills);

  const roles = arrayToCsv(
    (matchedUser?.roleIds || [])
      .map((id: string) => allRoles.find((role: Role) => role.id === id))
      .filter((role: Role | undefined) => role && !role.disabledAt)
      .map((role: Role) => role.name)
  );
  const teams = arrayToCsv(map(userTeams[matchedUser.id], ({ name }) => name));
  const inboxes = arrayToCsv(map(userRoutingGroups[matchedUser.id], ({ name }) => name));
  const skillsNames = arrayToCsv(
    map(userSkills, skill => {
      const unique = filter(skills, { name: skill.name }).length === 1;
      return unique ? escapeSkillName(skill.name) : skill.nameWithAncestry;
    })
  );

  return {
    name: agentUser.name,
    email: agentUser.email,
    roles,
    inboxes,
    teams,
    deactivated: agentUser.disabledAt ? 'Yes' : 'No',
    stationid: agentUser.voiceConfiguration?.stationId || '',
    skills: skillsNames,
  };
};

export const arrayToCsv = items => Papa.unparse({ data: items });
export const objToCsv = data => Papa.unparse(data);

export const mergeSkillsWithHierarchy = (skillHierarchy: SkillAncestry[], skills: Skill[]) => {
  const hierarchy = skillHierarchyToAncestries(skillHierarchy);

  return map(skills, skill => {
    return {
      ...skill,
      nameWithAncestry: getAncestralName(hierarchy, skills, skill),
    };
  });
};

const getAncestralName = (hierarchy: Ancestry[], skills: Skill[], skill: Skill) => {
  return getAncestralNames(hierarchy, skills, skill).map(escapeSkillName).join('>');
};

const escapeSkillName = (skillName: string) => {
  if (skillName.includes('>')) {
    return `"${skillName}"`;
  }

  return `${arrayToCsv([skillName])}`;
};

export default agentUsersListToCsv;
